import React, { memo, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { residenceMapMarkersQuery } from '../../../api/queries';
import { ResidenceMapMarkersQuery } from '../../../graphql/graphql';
import { ResidencesMapContainer } from './styled';
import { Marker } from '../../ItemsMap/types';
import {
  buildMarkers,
  calculateMapBounds
} from '../../../utils/helpers/residencesMap';
import { MapResidencesList } from './MapResidencesList';
import { ItemsMap } from '../../ItemsMap';
import { ItemsMapPlaceholder } from '../../ItemsMap/ItemsMapPlaceholder';
import { useLazyMapRender } from '../../../hooks/useLazyMapRender';

interface ResidencesMapProps {
  isPerUnit: boolean;
}

export const ResidencesMap: React.FC<ResidencesMapProps> = memo(
  ({ isPerUnit }) => {
    const { t } = useTranslation();
    const { mapBlockRef, isMapVisible } = useLazyMapRender();

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [zoom, setZoom] = useState<number>();
    const [activeSelection, setActiveSelection] = useState<Marker | null>(null);

    const { data, loading } = useQuery<ResidenceMapMarkersQuery>(
      residenceMapMarkersQuery
    );

    const markers = useMemo(
      () => buildMarkers(data?.residenceMapMarkers, zoom),
      [data?.residenceMapMarkers, zoom]
    );
    const initialMapBounds = useMemo(
      () => calculateMapBounds(data?.residenceMapMarkers),
      [data?.residenceMapMarkers]
    );

    const activeSelectionIds = useMemo(() => {
      if (!activeSelection) return [];

      return activeSelection.type === 'ClusterMapMarker'
        ? activeSelection.itemsIds || []
        : [activeSelection.id];
    }, [activeSelection]);

    const markersMap = useMemo(() => {
      if (!data?.residenceMapMarkers) return new Map();

      return new Map(data.residenceMapMarkers.map(m => [m.residenceId, m]));
    }, [data?.residenceMapMarkers]);

    const itemsSelection = useMemo(
      () => activeSelectionIds.map(id => markersMap.get(id)).filter(Boolean),
      [activeSelectionIds, markersMap]
    );

    return (
      <ResidencesMapContainer
        $markersLoading={loading}
        ref={mapBlockRef}
        id="items-map"
      >
        {isMapVisible ? (
          <ItemsMap
            title={t('sections.residences.title')}
            zoom={zoom}
            map={map}
            mapBounds={initialMapBounds}
            mapMarkers={markers}
            activeSelection={activeSelection}
            setMap={setMap}
            setZoom={setZoom}
            setActiveSelection={setActiveSelection}
          >
            {activeSelection && (
              <MapResidencesList items={itemsSelection} isPerUnit={isPerUnit} />
            )}
          </ItemsMap>
        ) : (
          <ItemsMapPlaceholder />
        )}
      </ResidencesMapContainer>
    );
  }
);

ResidencesMap.displayName = 'ResidencesMap';
